import AddRounded from "@mui/icons-material/AddRounded";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import ManageSearchRounded from "@mui/icons-material/ManageSearchRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import {
    Alert,
    Box,
    Collapse,
    Pagination,
    Paper,
    Skeleton,
    Stack,
    Typography,
} from "@mui/material";
import { SubscriptionCadence } from "components/Conversations/Views/Drawer/components/Subscriptions/helpers";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxChip from "components/UI/AcxChip";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxToggleButton from "components/UI/AcxToggleButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import { observer } from "mobx-react";
import moment from "moment";
import React from "react";
import {
    KboFlowSubscription,
    KboFlowSubscriptionsService,
    KboFlowTenantQuestionSubscription,
} from "services/KboFlowSubscriptionsService";
import { UserForSharing, UserService } from "services/UserService";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import { BannerHighlight, SubscriptionsBanner } from "./SubscriptionsBanner";

type Props = {
    /** The admin route supplies this explicitly; KBo Settings uses the selected tenant. */
    organizationId?: string;
    breadcrumbs?: React.ReactNode;
};

type UserOption = UserForSharing & {
    label: string;
};

type SubscriberIdentity = {
    userId: string;
    userName?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
};

/** One person and every subscription they have in the tenant. */
type Subscriber = {
    userId: string;
    displayName: string;
    email?: string;
    kboFlow?: KboFlowSubscription;
    questions: KboFlowTenantQuestionSubscription[];
};

const PAGE_SIZE = 10;

const cadenceLabels: Record<SubscriptionCadence, string> = {
    [SubscriptionCadence.Daily]: "Daily",
    [SubscriptionCadence.Weekly]: "Weekly",
    [SubscriptionCadence.Monthly]: "Monthly",
};

const cadenceOptions = [
    {
        id: "kbo-subscription-cadence-weekly",
        value: SubscriptionCadence.Weekly,
        element: "Weekly",
    },
    {
        id: "kbo-subscription-cadence-monthly",
        value: SubscriptionCadence.Monthly,
        element: "Monthly",
    },
    {
        id: "kbo-subscription-cadence-daily",
        value: SubscriptionCadence.Daily,
        element: "Daily",
    },
];

const formatDate = (value?: string | null) =>
    value ? moment(value).format("MMM D, YYYY") : undefined;

const getDisplayName = (identity: SubscriberIdentity) =>
    `${identity.firstName ?? ""} ${identity.lastName ?? ""}`.trim() ||
    identity.userName ||
    identity.email ||
    identity.userId;

const getInitials = (displayName: string) =>
    displayName
        .split(/[\s@.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join("");

const pluralize = (count: number, singular: string, plural: string) =>
    `${count} ${count === 1 ? singular : plural}`;

const cadenceDescription = (cadence: SubscriptionCadence) => {
    switch (cadence) {
        case SubscriptionCadence.Monthly:
            return "The selected users will receive one automatic KBo Flow email each month.";
        case SubscriptionCadence.Daily:
            return "The selected users will receive one automatic KBo Flow email each day.";
        default:
            return "The selected users will receive one automatic KBo Flow email each week.";
    }
};

const groupBySubscriber = (
    kboFlowSubscriptions: KboFlowSubscription[],
    questionSubscriptions: KboFlowTenantQuestionSubscription[],
): Subscriber[] => {
    const subscribers = new Map<string, Subscriber>();
    const subscriberFor = (identity: SubscriberIdentity) => {
        let subscriber = subscribers.get(identity.userId);
        if (!subscriber) {
            subscriber = {
                userId: identity.userId,
                displayName: getDisplayName(identity),
                email: identity.email,
                questions: [],
            };
            subscribers.set(identity.userId, subscriber);
        }
        return subscriber;
    };

    kboFlowSubscriptions.forEach((subscription) => {
        subscriberFor(subscription).kboFlow = subscription;
    });
    questionSubscriptions.forEach((subscription) => {
        subscriberFor(subscription).questions.push(subscription);
    });

    return Array.from(subscribers.values()).sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
    );
};

/**
 * "All Subscriptions" tab under KBo Settings (also rendered by Admin). Lists every person in the
 * tenant with a KBo Flow or question subscription, one expandable row per person. Ultra users can
 * enroll people in KBo Flow and change or remove those enrollments; question subscriptions are
 * read-only here because each belongs to the person who wrote the question.
 */
export const SubscriptionsPage = observer(function SubscriptionsPage({
    organizationId,
    breadcrumbs,
}: Props) {
    const authStore = useStore(AuthStore);
    const messageStore = useStore(MessageStore);
    const subscriptionService = React.useMemo(
        () => new KboFlowSubscriptionsService(),
        [],
    );
    const userService = React.useMemo(() => new UserService(), []);
    const resolvedOrganizationId =
        organizationId ??
        authStore.orgStore?.selectedOrganization?.id ??
        authStore.effectiveOrgId;
    // Admin can render this for an organization other than the selected tenant, so only use the
    // selected organization's name when it is the one being shown.
    const selectedOrganization = authStore.orgStore?.selectedOrganization;
    const organizationName =
        selectedOrganization?.id === resolvedOrganizationId
            ? selectedOrganization?.name
            : undefined;
    const canManage = authStore.isUserUltra();

    const [kboFlowSubscriptions, setKboFlowSubscriptions] = React.useState<
        KboFlowSubscription[]
    >([]);
    const [questionSubscriptions, setQuestionSubscriptions] = React.useState<
        KboFlowTenantQuestionSubscription[]
    >([]);
    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState<string>();
    const [users, setUsers] = React.useState<UserForSharing[]>([]);

    const [search, setSearch] = React.useState("");
    const [page, setPage] = React.useState(1);
    const [expandedUserIds, setExpandedUserIds] = React.useState<Set<string>>(
        () => new Set(),
    );

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editing, setEditing] = React.useState<KboFlowSubscription>();
    const [selectedUsers, setSelectedUsers] = React.useState<UserOption[]>([]);
    const [cadence, setCadence] = React.useState(SubscriptionCadence.Weekly);
    const [saving, setSaving] = React.useState(false);
    const [deleting, setDeleting] = React.useState<KboFlowSubscription>();
    const [deleteLoading, setDeleteLoading] = React.useState(false);

    const loadSubscriptions = React.useCallback(
        async (abortSignal?: AbortSignal) => {
            setLoadError(undefined);
            try {
                const [kboFlow, questions] = await Promise.all([
                    subscriptionService.list(abortSignal),
                    subscriptionService.listQuestions(abortSignal),
                ]);
                if (abortSignal?.aborted) return;
                setKboFlowSubscriptions(kboFlow);
                setQuestionSubscriptions(questions);
            } catch (error) {
                if (!abortSignal?.aborted) {
                    setLoadError(
                        error instanceof Error
                            ? error.message
                            : "Subscriptions could not be loaded.",
                    );
                }
            } finally {
                if (!abortSignal?.aborted) setLoading(false);
            }
        },
        [subscriptionService],
    );

    React.useEffect(() => {
        const controller = new AbortController();
        void loadSubscriptions(controller.signal);
        return () => controller.abort();
    }, [loadSubscriptions]);

    React.useEffect(() => {
        if (!canManage || !resolvedOrganizationId) return undefined;

        let cancelled = false;
        userService
            .getUsersForSharing(resolvedOrganizationId)
            .then((result) => {
                if (!cancelled) setUsers(result);
            })
            .catch(() => {
                if (!cancelled) setUsers([]);
            });

        return () => {
            cancelled = true;
        };
    }, [canManage, resolvedOrganizationId, userService]);

    const subscribers = React.useMemo(
        () => groupBySubscriber(kboFlowSubscriptions, questionSubscriptions),
        [kboFlowSubscriptions, questionSubscriptions],
    );

    const filteredSubscribers = React.useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return subscribers;
        return subscribers.filter(
            (subscriber) =>
                subscriber.displayName.toLowerCase().includes(term) ||
                subscriber.email?.toLowerCase().includes(term),
        );
    }, [search, subscribers]);

    const pageCount = Math.max(
        1,
        Math.ceil(filteredSubscribers.length / PAGE_SIZE),
    );
    const currentPage = Math.min(page, pageCount);
    const pageSubscribers = filteredSubscribers.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE,
    );

    const eligibleUsers = React.useMemo(() => {
        const enrolledUserIds = new Set(
            kboFlowSubscriptions.map((subscription) => subscription.userId),
        );
        return users
            .filter((user) => !enrolledUserIds.has(user.userId))
            .map((user) => ({
                ...user,
                label: user.email || user.userName,
            }));
    }, [kboFlowSubscriptions, users]);

    const toggleExpanded = (userId: string) =>
        setExpandedUserIds((current) => {
            const next = new Set(current);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });

    const closeDialog = () => {
        if (saving) return;
        setDialogOpen(false);
        setEditing(undefined);
        setSelectedUsers([]);
        setCadence(SubscriptionCadence.Weekly);
    };

    const openCreate = () => {
        setEditing(undefined);
        setSelectedUsers([]);
        setCadence(SubscriptionCadence.Weekly);
        setDialogOpen(true);
    };

    const openEdit = (subscription: KboFlowSubscription) => {
        setEditing(subscription);
        setSelectedUsers([]);
        setCadence(subscription.cadence);
        setDialogOpen(true);
    };

    const saveSubscription = async () => {
        if (!editing && selectedUsers.length === 0) return;

        setSaving(true);
        try {
            if (editing) {
                await subscriptionService.update(editing.id, {
                    cadence,
                    workflowName: editing.workflowName,
                    workflowInput: editing.workflowInput,
                });
                messageStore.logMessage("Subscription updated.", "success");
            } else {
                await subscriptionService.createBatch({
                    userIds: selectedUsers.map((user) => user.userId),
                    cadence,
                });
                messageStore.logMessage("Subscriptions added.", "success");
            }

            setDialogOpen(false);
            setEditing(undefined);
            setSelectedUsers([]);
            await loadSubscriptions();
        } catch (error) {
            messageStore.logError(
                error instanceof Error
                    ? error.message
                    : "The subscription could not be saved.",
            );
        } finally {
            setSaving(false);
        }
    };

    const deleteSubscription = async () => {
        if (!deleting) return;

        setDeleteLoading(true);
        try {
            await subscriptionService.remove(deleting.id);
            messageStore.logMessage("Subscription deleted.", "success");
            setDeleting(undefined);
            await loadSubscriptions();
        } catch (error) {
            messageStore.logError(
                error instanceof Error
                    ? error.message
                    : "The subscription could not be deleted.",
            );
        } finally {
            setDeleteLoading(false);
        }
    };

    const firstShown = (currentPage - 1) * PAGE_SIZE + 1;
    const lastShown = Math.min(
        currentPage * PAGE_SIZE,
        filteredSubscribers.length,
    );

    return (
        <Stack rowGap={2} padding={3} width="100%">
            {breadcrumbs}

            <SubscriptionsBanner
                id="kbo-all-subscriptions-banner"
                title={
                    organizationName
                        ? `${organizationName} Subscriptions`
                        : "All Subscriptions"
                }
                loading={loading}
                summary={
                    <>
                        <BannerHighlight>
                            {pluralize(subscribers.length, "person", "people")}
                        </BannerHighlight>{" "}
                        {subscribers.length === 1 ? "has" : "have"}{" "}
                        <BannerHighlight>
                            {pluralize(
                                kboFlowSubscriptions.length +
                                    questionSubscriptions.length,
                                "subscription",
                                "subscriptions",
                            )}
                        </BannerHighlight>{" "}
                        delivering KBo Flow research to their inbox.
                    </>
                }
                action={
                    canManage && (
                        <AcxButton
                            id="kbo-all-subscriptions-add"
                            color="white"
                            variant="contained"
                            startIcon={<AddRounded />}
                            onClick={openCreate}
                            disabled={eligibleUsers.length === 0}
                            disabledTooltip={
                                eligibleUsers.length === 0
                                    ? "Every eligible tenant user is already subscribed to KBo Flow."
                                    : undefined
                            }
                        >
                            Add Subscription
                        </AcxButton>
                    )
                }
            />

            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                rowGap={1}
                columnGap={2}
                marginTop={1}
            >
                <Typography
                    component="h2"
                    sx={{
                        fontSize: 18,
                        lineHeight: "26px",
                        fontWeight: 600,
                        color: "#111827",
                    }}
                >
                    People
                </Typography>
                <Box width={{ xs: "100%", sm: 320 }}>
                    <AcxMainTextField
                        id="kbo-all-subscriptions-search"
                        placeholderText="Search by name or email"
                        value={search}
                        onChange={(event) => {
                            setSearch(event.target.value);
                            setPage(1);
                        }}
                        startAdornment={
                            <SearchRounded
                                sx={{ fontSize: 18, color: "#6B7280" }}
                            />
                        }
                        fullWidth
                    />
                </Box>
            </Stack>

            {loadError && <Alert severity="error">{loadError}</Alert>}

            {loading ? (
                <Stack rowGap={1}>
                    {[0, 1, 2, 3].map((index) => (
                        <Skeleton key={index} variant="rounded" height={64} />
                    ))}
                </Stack>
            ) : (
                !loadError &&
                (filteredSubscribers.length === 0 ? (
                    <Box
                        id="kbo-all-subscriptions-empty"
                        sx={{
                            border: "1px dashed #D1D5DB",
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            padding: 2,
                            textAlign: "center",
                            fontSize: 14,
                            color: "#6B7280",
                        }}
                    >
                        {search.trim()
                            ? "No one matches that search."
                            : "No one in this organization has a subscription yet."}
                    </Box>
                ) : (
                    <Stack rowGap={1}>
                        {pageSubscribers.map((subscriber) => (
                            <SubscriberRow
                                key={subscriber.userId}
                                subscriber={subscriber}
                                expanded={expandedUserIds.has(
                                    subscriber.userId,
                                )}
                                onToggle={() =>
                                    toggleExpanded(subscriber.userId)
                                }
                                canManage={canManage}
                                onEdit={openEdit}
                                onDelete={setDeleting}
                            />
                        ))}
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            rowGap={1}
                            paddingTop={1}
                        >
                            <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                                {`Showing ${firstShown}–${lastShown} of ${pluralize(
                                    filteredSubscribers.length,
                                    "person",
                                    "people",
                                )}`}
                            </Typography>
                            {pageCount > 1 && (
                                <Pagination
                                    id="kbo-all-subscriptions-pagination"
                                    count={pageCount}
                                    page={currentPage}
                                    color="primary"
                                    shape="rounded"
                                    onChange={(_event, value) => setPage(value)}
                                />
                            )}
                        </Stack>
                    </Stack>
                ))
            )}

            <AcxDialog
                isOpen={dialogOpen}
                onClose={closeDialog}
                title={editing ? "Edit Subscription" : "Add Subscription"}
                subTitle={
                    editing
                        ? "Update the cadence for this recurring KBo Flow research email."
                        : "Enroll people in recurring KBo Flow research emails."
                }
                maxWidth="sm"
                fullWidth
                buttonsDisabled={saving}
                dialogContentChildren={
                    <Stack rowGap={3} paddingTop={1}>
                        {editing ? (
                            <Stack rowGap={0.5}>
                                <Typography fontWeight={600}>
                                    Recipient
                                </Typography>
                                <Typography>
                                    {getDisplayName(editing)}
                                </Typography>
                                {editing.email && (
                                    <Typography color="text.secondary">
                                        {editing.email}
                                    </Typography>
                                )}
                            </Stack>
                        ) : (
                            <AcxSelectMulti<UserOption>
                                id="kbo-subscription-recipients"
                                inputLabel="Recipients"
                                placeholder="Select tenant users by email"
                                options={eligibleUsers}
                                defaultValue={selectedUsers}
                                onChange={(options) =>
                                    setSelectedUsers(options ?? [])
                                }
                                valueField="userId"
                                labelField="label"
                                fullWidth
                                isClearable
                                containerHeight="auto"
                                maxContainerHeight="96px"
                                alignControls="flex-start"
                            />
                        )}
                        <AcxToggleButton
                            id="kbo-subscription-cadence"
                            inputLabel="Cadence"
                            defaultValue={cadence}
                            exclusive
                            items={cadenceOptions}
                            onChange={(value) => {
                                if (value !== null) {
                                    setCadence(value as SubscriptionCadence);
                                }
                            }}
                            groupFlexGrow={1}
                        />
                        <Paper
                            variant="outlined"
                            sx={(theme) => ({
                                padding: theme.spacing(2),
                                backgroundColor: theme.palette.gray[50],
                            })}
                        >
                            <Typography color="text.secondary">
                                {cadenceDescription(cadence)}
                            </Typography>
                        </Paper>
                    </Stack>
                }
            >
                <AcxButton
                    color="primary"
                    id="save-kbo-subscription"
                    onClick={saveSubscription}
                    loading={saving}
                    disabled={
                        saving || (!editing && selectedUsers.length === 0)
                    }
                >
                    {editing ? "Save" : "Add Subscription"}
                </AcxButton>
            </AcxDialog>

            <AcxConfirmationDialog
                isOpen={Boolean(deleting)}
                onClose={() => {
                    if (!deleteLoading) setDeleting(undefined);
                }}
                title="Delete Subscription"
                content={`Stop recurring KBo Flow emails for ${
                    deleting ? getDisplayName(deleting) : "this user"
                }?`}
                confirmButtonText="Delete Subscription"
                confirmButtonId="confirm-delete-kbo-subscription"
                onConfirm={deleteSubscription}
                isLoading={deleteLoading}
            />
        </Stack>
    );
});

type SubscriberRowProps = {
    subscriber: Subscriber;
    expanded: boolean;
    onToggle: () => void;
    canManage: boolean;
    onEdit: (subscription: KboFlowSubscription) => void;
    onDelete: (subscription: KboFlowSubscription) => void;
};

function SubscriberRow({
    subscriber,
    expanded,
    onToggle,
    canManage,
    onEdit,
    onDelete,
}: SubscriberRowProps) {
    const { kboFlow, questions } = subscriber;
    const detailsId = `kbo-subscriber-${subscriber.userId}-details`;

    return (
        <Paper
            id={`kbo-subscriber-${subscriber.userId}`}
            variant="outlined"
            sx={{
                borderRadius: "10px",
                borderColor: expanded ? "#3364D6" : "#E5E7EB",
                overflow: "hidden",
            }}
        >
            <Stack
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-controls={detailsId}
                direction="row"
                alignItems="center"
                columnGap={1.5}
                onClick={onToggle}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onToggle();
                    }
                }}
                sx={{
                    padding: "12px 16px",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#F9FAFB" },
                    "&:focus-visible": {
                        outline: "2px solid #3364D6",
                        outlineOffset: "-2px",
                    },
                }}
            >
                <ExpandMoreRounded
                    sx={{
                        color: "#6B7280",
                        transition: "transform 150ms",
                        transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                    }}
                />
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        flexShrink: 0,
                        borderRadius: "50%",
                        backgroundColor: "#EEF3FD",
                        color: "#3364D6",
                        display: "grid",
                        placeItems: "center",
                        fontSize: 12,
                        fontWeight: 600,
                    }}
                >
                    {getInitials(subscriber.displayName)}
                </Box>
                <Stack minWidth={0} flex={1}>
                    <Typography
                        noWrap
                        sx={{
                            fontSize: 14,
                            lineHeight: "20px",
                            fontWeight: 600,
                            color: "#111827",
                        }}
                    >
                        {subscriber.displayName}
                    </Typography>
                    {subscriber.email && (
                        <Typography
                            noWrap
                            sx={{
                                fontSize: 13,
                                lineHeight: "18px",
                                color: "#6B7280",
                            }}
                        >
                            {subscriber.email}
                        </Typography>
                    )}
                </Stack>
                <Stack
                    direction="row"
                    columnGap={1}
                    flexShrink={0}
                    display={{ xs: "none", sm: "flex" }}
                >
                    {kboFlow && (
                        <AcxChip
                            size="small"
                            color="blue"
                            label={`KBo Flow · ${
                                cadenceLabels[kboFlow.cadence]
                            }`}
                        />
                    )}
                    {questions.length > 0 && (
                        <AcxChip
                            size="small"
                            color="gray"
                            label={pluralize(
                                questions.length,
                                "question",
                                "questions",
                            )}
                        />
                    )}
                </Stack>
            </Stack>

            <Collapse in={expanded} unmountOnExit>
                <Stack
                    id={detailsId}
                    sx={{
                        borderTop: "1px solid #E5E7EB",
                        backgroundColor: "#F9FAFB",
                    }}
                >
                    {kboFlow && (
                        <SubscriptionItem
                            icon="kboFlow"
                            title="KBo Flow"
                            description="KBo picks what to research from this person's data and context."
                            cadence={kboFlow.cadence}
                            lastRun={kboFlow.lastRun}
                            nextRun={kboFlow.nextRun}
                            actions={
                                canManage && (
                                    <>
                                        <AcxButton
                                            id={`kbo-subscription-edit-${kboFlow.id}`}
                                            size="small"
                                            color="white"
                                            variant="outlined"
                                            startIcon={<EditOutlined />}
                                            onClick={() => onEdit(kboFlow)}
                                        >
                                            Edit
                                        </AcxButton>
                                        <AcxButton
                                            id={`kbo-subscription-delete-${kboFlow.id}`}
                                            size="small"
                                            color="white"
                                            variant="outlined"
                                            startIcon={<DeleteOutlineRounded />}
                                            onClick={() => onDelete(kboFlow)}
                                        >
                                            Delete
                                        </AcxButton>
                                    </>
                                )
                            }
                        />
                    )}
                    {questions.map((question) => (
                        <SubscriptionItem
                            key={question.id}
                            icon="question"
                            title={question.name}
                            description={question.questionText}
                            cadence={question.cadence}
                            lastRun={question.lastRun}
                            nextRun={question.nextRun}
                        />
                    ))}
                </Stack>
            </Collapse>
        </Paper>
    );
}

type SubscriptionItemProps = {
    icon: "kboFlow" | "question";
    title: string;
    description: string;
    cadence: SubscriptionCadence;
    lastRun?: string | null;
    nextRun?: string | null;
    actions?: React.ReactNode;
};

function SubscriptionItem({
    icon,
    title,
    description,
    cadence,
    lastRun,
    nextRun,
    actions,
}: SubscriptionItemProps) {
    const meta = [
        cadenceLabels[cadence],
        lastRun && `Last run ${formatDate(lastRun)}`,
        nextRun && `Next run ${formatDate(nextRun)}`,
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        <Stack
            direction="row"
            alignItems="center"
            columnGap={1.5}
            rowGap={1}
            flexWrap="wrap"
            sx={{
                padding: "12px 16px 12px 60px",
                "& + &": { borderTop: "1px solid #E5E7EB" },
            }}
        >
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    borderRadius: "8px",
                    display: "grid",
                    placeItems: "center",
                    ...(icon === "kboFlow"
                        ? {
                              background:
                                  "linear-gradient(135deg, #3564D5 0%, #349AC7 55%, #32D1B7 100%)",
                              color: "#FFFFFF",
                          }
                        : { backgroundColor: "#F3F4F6", color: "#374151" }),
                }}
            >
                {icon === "kboFlow" ? (
                    <AutoAwesomeOutlined sx={{ fontSize: 18 }} />
                ) : (
                    <ManageSearchRounded sx={{ fontSize: 20 }} />
                )}
            </Box>
            <Stack flex={1} minWidth={220} rowGap={0.25}>
                <Typography
                    sx={{
                        fontSize: 14,
                        lineHeight: "20px",
                        fontWeight: 600,
                        color: "#111827",
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        fontSize: 13,
                        lineHeight: "19px",
                        color: "#4B5563",
                        wordBreak: "break-word",
                    }}
                >
                    {description}
                </Typography>
                <Typography
                    sx={{ fontSize: 13, lineHeight: "18px", color: "#6B7280" }}
                >
                    {meta}
                </Typography>
            </Stack>
            {actions && (
                <Stack direction="row" columnGap={1} flexShrink={0}>
                    {actions}
                </Stack>
            )}
        </Stack>
    );
}
